import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './AuthContext';
import { clearCurrentWorkspaceId, getCurrentWorkspaceId, setCurrentWorkspaceId } from '@/services/workspaceState';
import { resetDbSync } from '@/services/dbSyncService';

const ADMIN_WS_OVERRIDE_KEY = 'wf_admin_workspace_override';

interface WorkspaceContextType {
  workspaceId: string | null;
  isSuperAdmin: boolean;
  isLoading: boolean;
  accountStatus: string | null;
  setWorkspaceId: (id: string) => void;
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

export function WorkspaceProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [workspaceId, setWorkspaceId] = useState<string | null>(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [accountStatus, setAccountStatus] = useState<string | null>(null);
  const [resolvedUserId, setResolvedUserId] = useState<string | null>(null);

  const isLoading = !!user && resolvedUserId !== user.id;

  useEffect(() => {
    if (!user) {
      clearCurrentWorkspaceId();
      setWorkspaceId(null);
      setIsSuperAdmin(false);
      setAccountStatus(null);
      setResolvedUserId(null);
      return;
    }

    if (resolvedUserId === user.id) return;

    let cancelled = false;

    const resolve = async (attempt = 0) => {
      const timeout = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Workspace resolution timeout')), 10000)
      );

      try {
        await Promise.race([timeout, (async () => {
          const { data: platformUser } = await supabase
            .from('platform_users')
            .select('role, is_active')
            .eq('user_id', user.id)
            .maybeSingle();

          if (cancelled) return;

          const isAdmin = platformUser?.role === 'super_admin' && platformUser.is_active;
          setIsSuperAdmin(isAdmin);

          const adminOverride = isAdmin ? localStorage.getItem(ADMIN_WS_OVERRIDE_KEY) : null;

          let wsId: string;
          if (adminOverride) {
            wsId = adminOverride;
          } else {
            const { data: membership } = await supabase
              .from('workspace_members')
              .select('workspace_id')
              .eq('user_id', user.id)
              .eq('is_active', true)
              .limit(1)
              .maybeSingle();

            if (cancelled) return;

            if (!membership && !isAdmin) {
              // Self-registered user with no workspace yet — auto-provision
              console.log('[WorkspaceContext] No workspace found, calling self-register...');
              try {
                const { data: regData, error: regError } = await supabase.functions.invoke('self-register');
                if (regError) {
                  console.error('[WorkspaceContext] self-register error:', regError);
                  throw regError;
                } else {
                  if (!regData?.workspace_id) throw new Error('self-register returned no workspace_id');
                  wsId = regData.workspace_id;
                  console.log('[WorkspaceContext] Auto-provisioned workspace:', wsId);
                }
              } catch (e) {
                console.error('[WorkspaceContext] self-register call failed:', e);
                throw e;
              }
            } else {
              if (!membership?.workspace_id) throw new Error('No active workspace membership found');
              wsId = membership.workspace_id;
            }
          }

          setCurrentWorkspaceId(wsId);
          setWorkspaceId(wsId);

          const { data: account } = await supabase
            .from('platform_accounts')
            .select('status')
            .eq('workspace_id', wsId)
            .maybeSingle();

          if (cancelled) return;
          setAccountStatus(account?.status || 'active');
          setResolvedUserId(user.id);
        })()]);
      } catch (err) {
        if (cancelled) return;
        console.warn('[WorkspaceContext] Error resolving workspace (attempt', attempt + 1, '):', err);
        if (attempt < 2) {
          setTimeout(() => { if (!cancelled) resolve(attempt + 1); }, 2000 * (attempt + 1));
          return;
        }
        console.error('[WorkspaceContext] All retries failed, using defaults');
        clearCurrentWorkspaceId();
        setWorkspaceId(null);
        setAccountStatus('active');
        setResolvedUserId(user.id);
      }
    };

    resolve();
    return () => { cancelled = true; };
  }, [user, resolvedUserId]);

  const handleSetWorkspaceId = (id: string) => {
    // If super_admin is switching workspaces, persist override
    if (isSuperAdmin) {
      localStorage.setItem(ADMIN_WS_OVERRIDE_KEY, id);
    }
    // Clear all workspace-specific cached data before switching
    resetDbSync();
    setCurrentWorkspaceId(id);
    setWorkspaceId(id);
  };

  return (
    <WorkspaceContext.Provider value={{ workspaceId, isSuperAdmin, isLoading, accountStatus, setWorkspaceId: handleSetWorkspaceId }}>
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  const context = useContext(WorkspaceContext);
  if (!context) throw new Error('useWorkspace must be used within WorkspaceProvider');
  return context;
}
